import React from 'react';
import { useSelector } from 'react-redux';
import { MenuItem, TextField } from '@material-ui/core';
import AutoComplete from '@material-ui/lab/Autocomplete';
import { Order, DataType, getOrderLabel, AwardingRuleParameterType } from '../../../../Enums';
import StyledSelect from '../StyledSelect';
import InputTitle from '../InputTitle';

import './Step2-SortingRuleSection.scss';

const typePrefix = ["Long", "Date", "Double", "Time"];

/**
 * @param {{
 *  currentAwardingRule: ApiAdmin.IBodyAwardingRules,
 *  onChange: Function,
 *  isDisabled: boolean,
 *  fieldValidationErrors: {[Key: string] : boolean},
 * }} props
 */
function DataviewParameter(props) {
    const {
        currentAwardingRule,
        onChange = () => { },
        isDisabled,
        fieldValidationErrors,
    } = props;

    /** @type {EmploymentBillboard.AdminStore} */
    // @ts-ignore
    const { adminAppData, adminRuleSet } = useSelector((state) => state);
    const { awardingRules } = adminRuleSet;
    const { dataviews, customDateFormat: customDateFormatList } = adminAppData;
    const { dataviewId, dataviewKeyAlias, dataType, customDateFormat } = currentAwardingRule.dataviewInfos;

    /**
     * @typedef {{value: Order, label: string}} SelectOption
     * @type {SelectOption[]}
     */
    const orderOptions = Object.values(Order).map((x) => ({
        value: x, label: getOrderLabel(x),
    }));

    /**
    * @typedef {{value: string, label: string, disabled: boolean}} SelectDataviewFieldOption
    * @type {SelectDataviewFieldOption[]}
    */
    const dataviewFields = dataviews.find((x) => x.id === dataviewId)?.fields.map((x) => ({
        value: x.key,
        label: x.label,
        disabled: awardingRules.findIndex((parameter) => parameter.type === AwardingRuleParameterType.DataviewData && parameter.dataviewInfos.dataviewKeyAlias === x.key) !== -1,
    })) || [];

    /**
     * @typedef {{value: DataType | number, label: string}} DataTypeSelectOption
     * @type {DataTypeSelectOption[]}
     */
    const dataTypeOptions = [{ value: DataType.Numeric, type: DataType.Numeric, label: 'Number' }];
    for (const format of customDateFormatList) {
        dataTypeOptions.push({ value: format.id, type: format.dataType, label: `${typePrefix[format.dataType]} (${format.name})` });
    }

    const selectedDataviewKeyAlias = dataviewFields.find((x) => x.value === dataviewKeyAlias);
    const selectedCustomDateFormat = dataTypeOptions.find((x) => x.value === dataType || x.value === customDateFormat);
    const selectedOrder = orderOptions.find((x) => x.value === currentAwardingRule.order);

    return (
        <>
            <div className="rse--selection-field-container srs-fullWidth-container">
                <InputTitle>Dataview</InputTitle>
                <AutoComplete
                    id="dataview-select"
                    value={dataviews.find((x) => x.id === dataviewId)}
                    options={dataviews}
                    onChange={(_event, optionSelected) => onChange('dataviewId', optionSelected?.id)}
                    renderInput={(params) => (<TextField
                        {...params}
                        variant="outlined"
                        placeholder="Select a dataview"
                        style={{ paddingBottom: '20px' }}
                    />)}
                    getOptionLabel={(option) => option.name}
                    includeInputInList
                    disabled={isDisabled}
                    disableClearable
                />
                {
                    fieldValidationErrors.dataviewId ? <span style={{ color: 'red' }}>Please select a dataview</span> : null
                }
            </div>
            <div className="srs--dataviewCustomHyperfind-container">
                <div className="rse--selection-field-container srs-data-container">
                    <InputTitle>Dataview Field</InputTitle>
                    <StyledSelect
                        labelId="select-dataview-field-label"
                        id="select-dataview-field-type"
                        disabled={isDisabled}
                        renderValue={(/** @type {string} */
                            _value,
                        ) => selectedDataviewKeyAlias?.label}
                        value={selectedDataviewKeyAlias}
                        onChange={(/** @type {React.ChangeEvent<{ name?: string; value: string }>} */
                            event,
                        ) => onChange('dataviewKeyAlias', event.target.value)}
                    >
                        {dataviewFields.map((x) => (
                            <MenuItem
                                value={x.value}
                                key={`SelectItem_${x.label}`}
                                disabled={x.disabled}
                            >
                                {x.label}
                            </MenuItem>
                        ))}
                    </StyledSelect>
                    {
                        fieldValidationErrors.dataviewKeyAlias ? <span style={{ color: 'red' }}>Please select a dataview field</span> : null
                    }
                </div>
                <div className="rse--selection-field-container srs-dataAndOrderType-container">
                    <InputTitle>Data type</InputTitle>
                    <StyledSelect
                        labelId="select-data-type-label"
                        id="select-data-type-type"
                        disabled={isDisabled}
                        renderValue={(/** @type {string} */
                            _value,
                        ) => selectedCustomDateFormat?.label}
                        value={selectedCustomDateFormat}
                        onChange={(/** @type {React.ChangeEvent<{ name?: string; value: string }>} */
                            event,
                        ) => {
                            var selectedOption = dataTypeOptions.find(x => x.value === event.target.value);
                            onChange('dataType', selectedOption);
                        }}
                    >
                        {dataTypeOptions.map((x) => (
                            <MenuItem
                                value={x.value}
                                key={`SelectItem_${x.label}`}
                            >
                                {x.label}
                            </MenuItem>
                        ))}
                    </StyledSelect>
                    {
                        fieldValidationErrors.dataType || fieldValidationErrors.customDateFormat ? <span style={{ color: 'red' }}>Please select a data type</span> : null
                    }
                </div>
                <div className="rse--selection-field-container srs-dataAndOrderType-container">
                    <InputTitle>Order type</InputTitle>
                    <StyledSelect
                        labelId="select-sort-order-type-label"
                        id="select-sort-order-type-type"
                        disabled={isDisabled}
                        renderValue={(/** @type {string} */
                            _value,
                        ) => selectedOrder?.label}
                        value={selectedOrder}
                        onChange={(/** @type {React.ChangeEvent<{ name?: string; value: string }>} */
                            event,
                        ) => onChange('order', event.target.value)}
                    >
                        {orderOptions.map((x) => (
                            <MenuItem
                                value={x.value}
                                key={`SelectItem_${x.label}`}
                            >
                                {x.label}
                            </MenuItem>
                        ))}
                    </StyledSelect>
                    {
                        fieldValidationErrors.order ? <span style={{ color: 'red' }}>Please select a sorting order</span> : null
                    }
                </div>
            </div>
        </>
    );
}

export default DataviewParameter;
